export interface ItemCarrinho {
  id: string;
  cod: number;
  nome: string;
  quantidade: number;
  precoUnitario: number;
  desconto: number;
  /** Se o item entra no total e na finalização (default true). Itens desmarcados permanecem no carrinho após venda parcial. */
  marcado?: boolean;
  permiteDecimal?: boolean;
  /** Observação do item (mesma estrutura do Cardápio Digital / comandas_abertas_itens.observacao). */
  observacao?: string;
  /** Promoção configurável: itens escolhidos nos grupos (exibição no carrinho e envio na venda) */
  promocao_id?: number;
  itens_escolhidos?: Array<{ produto_id: number; nome_produto: string; quantidade: number; preco_unitario: number }>;
}

/** Limite compartilhado com o fluxo de observação do item no Caixa. */
export const OBSERVACAO_ITEM_MAX_LEN = 200;

export function normalizeItemObservacao(value: string | null | undefined): string {
  return (value ?? "").trim().slice(0, OBSERVACAO_ITEM_MAX_LEN);
}

/** Chave para mesclar linhas iguais (produto + nome + observação + escolhas da promoção). */
export function cartItemMergeKey(item: {
  cod: number;
  nome: string;
  observacao?: string | null;
  itens_escolhidos?: ItemCarrinho["itens_escolhidos"];
}): string {
  const escolhas = item.itens_escolhidos?.length
    ? [...item.itens_escolhidos]
        .map((e) => `${e.produto_id}:${e.quantidade}:${e.preco_unitario}:${e.nome_produto}`)
        .sort()
        .join("|")
    : "";
  const obs = normalizeItemObservacao(item.observacao);
  return `${item.cod}\x1f${item.nome}\x1f${obs}\x1f${escolhas}`;
}

/** Considera marcado para pagamento (undefined = sim, legado). */
export function itemIncluidoPagamento(item: ItemCarrinho): boolean {
  return item.marcado !== false;
}

export function totalItem(item: ItemCarrinho): number {
  return item.quantidade * item.precoUnitario - item.desconto;
}

export function totalItemSeMarcado(item: ItemCarrinho): number {
  return itemIncluidoPagamento(item) ? totalItem(item) : 0;
}

/**
 * Quantidade de “unidades” exibíveis no modo desagrupar.
 * Cada adicao fracionada permanece inteira, sem dividir seu peso/volume.
 */
export function cartItemUnitSlots(item: ItemCarrinho): number {
  if (item.permiteDecimal) return 1;
  const q = Math.round(item.quantidade);
  return Math.max(1, q);
}

/** Seleção por unidade: itemId → flags por índice 0..slots-1. */
export type CartUnitSelection = Record<string, boolean[]>;

export function syncCartUnitSelection(
  items: ItemCarrinho[],
  prev: CartUnitSelection,
): CartUnitSelection {
  const next: CartUnitSelection = {};
  for (const item of items) {
    const n = cartItemUnitSlots(item);
    const old = prev[item.id];
    const fallback = itemIncluidoPagamento(item);
    if (old && old.length === n) {
      next[item.id] = old;
    } else if (old && old.length > 0) {
      next[item.id] = Array.from({ length: n }, (_, i) => old[i] ?? fallback);
    } else {
      next[item.id] = Array.from({ length: n }, () => fallback);
    }
  }
  return next;
}

/** Inicializa seleção de unidades a partir do `marcado` atual (ao ativar desagrupar). */
export function unitSelectionFromMarcado(items: ItemCarrinho[]): CartUnitSelection {
  const next: CartUnitSelection = {};
  for (const item of items) {
    const n = cartItemUnitSlots(item);
    const on = itemIncluidoPagamento(item);
    next[item.id] = Array.from({ length: n }, () => on);
  }
  return next;
}

/** Ao desativar desagrupar: linha marcada só se todas as unidades estavam selecionadas. */
export function marcadoFromUnitSelection(
  items: ItemCarrinho[],
  selection: CartUnitSelection,
): ItemCarrinho[] {
  return items.map((item) => {
    const units = selection[item.id];
    const allOn = !units || units.length === 0 ? itemIncluidoPagamento(item) : units.every(Boolean);
    return { ...item, marcado: allOn };
  });
}

export function countSelectedUnits(item: ItemCarrinho, selection: CartUnitSelection): number {
  const units = selection[item.id];
  if (!units || units.length === 0) return itemIncluidoPagamento(item) ? cartItemUnitSlots(item) : 0;
  return units.filter(Boolean).length;
}

/**
 * Itens efetivos para pagamento (agrupado ou desagrupado).
 * No desagrupar, a quantidade reflete só as unidades selecionadas (mesmo `id` do item pai).
 */
export function buildItensParaPagamento(
  items: ItemCarrinho[],
  desagrupar: boolean,
  selection: CartUnitSelection,
): ItemCarrinho[] {
  if (!desagrupar) {
    return items.filter(itemIncluidoPagamento).map((i) => ({ ...i }));
  }
  const result: ItemCarrinho[] = [];
  for (const item of items) {
    const slots = cartItemUnitSlots(item);
    const selected = countSelectedUnits(item, selection);
    if (selected <= 0) continue;
    if (item.permiteDecimal || slots === 1) {
      result.push({ ...item });
      continue;
    }
    const desconto =
      item.quantidade > 0
        ? Math.round(((item.desconto * selected) / item.quantidade) * 100) / 100
        : 0;
    result.push({
      ...item,
      quantidade: selected,
      desconto,
    });
  }
  return result;
}

export function totalSelecionadoPagamento(
  items: ItemCarrinho[],
  desagrupar: boolean,
  selection: CartUnitSelection,
): number {
  return buildItensParaPagamento(items, desagrupar, selection).reduce((s, i) => s + totalItem(i), 0);
}

export function countItensSelecionadosPagamento(
  items: ItemCarrinho[],
  desagrupar: boolean,
  selection: CartUnitSelection,
): number {
  return buildItensParaPagamento(items, desagrupar, selection).reduce((s, i) => s + Math.round(i.quantidade * 1000), 0) / 1000;
}

export type MetodoPagamento = 'pix' | 'dinheiro' | 'credito' | 'debito';

export interface ValoresPagamento {
  pix: number;
  dinheiro: number;
  credito: number;
  debito: number;
  /** Valor recebido em dinheiro (para cálculo de valor em caixa e troco). */
  dinheiroRecebido?: number;
  /** Troco devolvido (para valor em caixa = recebido - troco). */
  troco?: number;
}
